import type { CatalogFaqItem } from '../../catalog/types';
import {
    availabilityLabel,
    catalogImageUrl,
    formatProductPrice,
} from '../lib/catalogImageUrl';
import { markdownParagraphs, renderInlineMarkdown } from '../lib/inlineMarkdown';

export interface ProductFichaPreviewModel {
    nombre: string;
    seoSlug: string;
    marca?: string;
    precio?: number | null;
    moneda?: string;
    disponibilidad?: string;
    disponibilidadTexto?: string;
    imagenBlobPath?: string | null;
    imagenPrincipal?: string;
    sourceImageUrl?: string | null;
    imagenAlt?: string;
    editorial?: string;
    descripcionLarga?: string;
    descripcionCorta?: string;
    veredicto?: string;
    pescaTags?: string[];
    specs?: Record<string, string>;
    faq?: CatalogFaqItem[];
    seoTitle?: string;
    seoDescription?: string;
}

function faqText(item: CatalogFaqItem): { q: string; a: string } {
    return {
        q: (item.q || item.question || '').trim(),
        a: (item.a || item.answer || '').trim(),
    };
}

export function ProductFichaPreview({ model }: { model: ProductFichaPreviewModel }) {
    const img = catalogImageUrl(model.imagenBlobPath, model.imagenPrincipal, model.sourceImageUrl);
    const alt = model.imagenAlt?.trim() || `${model.nombre} — Tienda AiPeces`;
    const editorial = model.editorial?.trim() || '';
    const manufacturer = (model.descripcionLarga || model.descripcionCorta || '').trim();
    const veredicto = model.veredicto?.trim() || '';
    const tags = (model.pescaTags || []).map((t) => t.trim()).filter(Boolean);
    const specs = Object.entries(model.specs || {}).filter(([, v]) => String(v || '').trim());
    const faqs = (model.faq || []).map(faqText).filter((f) => f.q && f.a);

    return (
        <article
            data-preview="product-ficha"
            className="rounded-2xl border border-zinc-200 bg-[#f7fafc] text-zinc-900 shadow-lg overflow-hidden"
        >
            <div className="px-4 py-2 bg-zinc-800 text-[11px] text-zinc-200 flex items-center justify-between gap-2">
                <span>Vista previa de la ficha pública</span>
                <span className="text-zinc-400 truncate">/tienda/p/{model.seoSlug || '…'}</span>
            </div>
            <div className="p-5 sm:p-6 space-y-5 max-w-[760px] mx-auto">
                <header>
                    {model.marca && (
                        <p className="text-xs uppercase tracking-wider text-zinc-500 mb-1">{model.marca}</p>
                    )}
                    <h1 className="text-[1.65rem] leading-tight font-extrabold m-0">{model.nombre || 'Sin nombre'}</h1>
                    <p className="text-lg font-bold text-sky-600 mt-2 mb-0">
                        {formatProductPrice(model.precio, model.moneda)}
                    </p>
                    <p className="text-sm text-zinc-500 mt-0.5">
                        {availabilityLabel(model.disponibilidad, model.disponibilidadTexto)}
                    </p>
                </header>

                {img ? (
                    <figure className="m-0 rounded-xl overflow-hidden bg-white border border-zinc-200">
                        <img src={img} alt={alt} className="w-full max-h-72 object-contain bg-white" />
                    </figure>
                ) : (
                    <div className="rounded-xl border border-dashed border-zinc-300 bg-white px-4 py-10 text-center text-sm text-zinc-400">
                        Sin imagen de producto
                    </div>
                )}

                {editorial && (
                    <section>
                        <h2 className="text-base font-extrabold mt-0 mb-2">Criterio AiPeces</h2>
                        {markdownParagraphs(editorial).map((p, i) => (
                            <p key={i} className="text-[15px] leading-relaxed m-0 mb-3 last:mb-0">
                                {renderInlineMarkdown(p)}
                            </p>
                        ))}
                    </section>
                )}

                {manufacturer && (
                    <section>
                        <h2 className="text-base font-extrabold mt-0 mb-2">Descripción</h2>
                        {markdownParagraphs(manufacturer).map((p, i) => (
                            <p key={i} className="text-[15px] leading-relaxed m-0 mb-3 last:mb-0 text-zinc-700">
                                {p}
                            </p>
                        ))}
                    </section>
                )}

                {(tags.length > 0 || veredicto) && (
                    <section>
                        <h2 className="text-base font-extrabold mt-0 mb-2">¿Para qué pesca te sirve?</h2>
                        {tags.length > 0 && (
                            <p className="flex flex-wrap gap-1.5 mb-2">
                                {tags.map((t) => (
                                    <span
                                        key={t}
                                        className="inline-flex rounded-full bg-sky-100 text-sky-800 text-xs font-semibold px-2.5 py-0.5"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </p>
                        )}
                        {veredicto && (
                            <p className="text-[15px] leading-relaxed m-0">{renderInlineMarkdown(veredicto)}</p>
                        )}
                    </section>
                )}

                {specs.length > 0 && (
                    <section>
                        <h2 className="text-base font-extrabold mt-0 mb-2">Ficha técnica</h2>
                        <dl className="m-0 grid gap-2 text-sm">
                            {specs.map(([label, value]) => (
                                <div key={label}>
                                    <dt className="inline font-bold">{label}:</dt>{' '}
                                    <dd className="inline m-0">{value}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                )}

                {faqs.length > 0 && (
                    <section>
                        <h2 className="text-base font-extrabold mt-0 mb-2">Preguntas frecuentes</h2>
                        <div className="space-y-3">
                            {faqs.map((f, i) => (
                                <div key={`${f.q}-${i}`}>
                                    <p className="font-bold text-sm m-0 mb-1">{f.q}</p>
                                    <p className="text-sm text-zinc-700 m-0">{f.a}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {!editorial && !manufacturer && !veredicto && (
                    <p className="text-sm text-zinc-500 m-0">
                        Aún no hay copy. Rellena Criterio AiPeces, veredicto o descripción para ver la ficha.
                    </p>
                )}

                {(model.seoTitle || model.seoDescription) && (
                    <div className="rounded-lg border border-zinc-200 bg-white p-3 text-xs text-zinc-600">
                        <p className="font-semibold text-zinc-800 m-0 mb-1">Cómo lo ve Google</p>
                        <p className="text-sky-700 m-0 mb-0.5">{model.seoTitle || model.nombre}</p>
                        <p className="m-0">{model.seoDescription || '—'}</p>
                    </div>
                )}
            </div>
        </article>
    );
}
